<template>
    <div>
        <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Buscar módulo ou permissão"
            dense
            outlined
            clearable
            hide-details
            class="mb-2"
        />
        <div class="caption grey--text mb-3">{{ totalSelected }} de {{ totalPermissions }} permissões selecionadas</div>

        <div v-for="module in filteredInstances" :key="module.id" class="mb-3">
            <v-card outlined>
                <v-card-title class="py-2">
                    <v-icon small color="grey darken-1" class="mr-2">mdi-shield-key-outline</v-icon>
                    <div>
                        <div class="subtitle-2">{{ module.name }}</div>
                        <div class="caption grey--text">{{ module.description }}</div>
                    </div>
                    <v-spacer />
                    <v-chip x-small label outlined :color="moduleColor(module)" class="mr-3">
                        {{ moduleCount(module) }}/{{ module.permissions.length }}
                    </v-chip>
                    <span class="caption grey--text mr-2">Todos</span>
                    <v-switch
                        :input-value="isModuleAll(module)"
                        color="green darken-2"
                        hide-details
                        dense
                        class="pa-0 ma-0"
                        @change="toggleModule(module, $event)"
                    />
                </v-card-title>
                <v-divider />
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" sm="4" v-for="perm in module.permissions" :key="perm.id">
                            <v-checkbox
                                v-model="selected"
                                :value="perm.id"
                                :label="perm.name"
                                color="green darken-3"
                                hide-details
                                dense
                                class="mt-0 pt-0 text-capitalize"
                                @change="apply"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </div>

        <div v-if="!filteredInstances.length" class="text-center grey--text py-6">
            <v-icon color="grey lighten-1">mdi-magnify-close</v-icon>
            <div class="caption mt-1">Nenhum resultado.</div>
        </div>
    </div>
</template>

<script setup>

import { defineProps, defineEmits, ref, computed, watch, onBeforeMount } from 'vue'
import { useStore } from '@/composables/useStore'

const store = useStore()

const props = defineProps({
    value: { type: Array, default: () => [] }
})
const emit = defineEmits(['input'])

const instances = ref([])
const search = ref('')
const selected = ref([...(props.value || [])])

watch(() => props.value, (val) => {
    const next = [...(val || [])]
    if (JSON.stringify(next) !== JSON.stringify(selected.value)) selected.value = next
})

const apply = () => emit('input', [...selected.value])

const allPermIds = computed(() => instances.value.flatMap((m) => m.permissions.map((p) => p.id)))
const totalPermissions = computed(() => allPermIds.value.length)
const totalSelected = computed(() => selected.value.filter((id) => allPermIds.value.includes(id)).length)

const moduleCount = (module) => module.permissions.filter((p) => selected.value.includes(p.id)).length
const isModuleAll = (module) => module.permissions.length > 0 && moduleCount(module) === module.permissions.length
const moduleColor = (module) => {
    const count = moduleCount(module)
    if (count === 0) return 'grey'
    return count === module.permissions.length ? 'success' : 'primary'
}

const toggleModule = (module, value) => {
    const ids = module.permissions.map((p) => p.id)
    if (value) {
        selected.value = Array.from(new Set([...selected.value, ...ids]))
    } else {
        selected.value = selected.value.filter((id) => !ids.includes(id))
    }
    apply()
}

const filteredInstances = computed(() => {
    const term = (search.value || '').toLowerCase()
    if (!term) return instances.value

    return instances.value
        .filter((m) => {
            return [m.name, m.description].some((v) => (v || '').toLowerCase().includes(term))
                || m.permissions.some((p) => (p.name || '').toLowerCase().includes(term))
        })
        .map((m) => {
            const moduleMatch = [m.name, m.description].some((v) => (v || '').toLowerCase().includes(term))
            if (moduleMatch) return m
            return { ...m, permissions: m.permissions.filter((p) => (p.name || '').toLowerCase().includes(term)) }
        })
})

onBeforeMount(async () => {
    const res = await store.dispatch('role/ActionInstances')
    instances.value = res.data || []
})

</script>
